import React, { useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";
import { ArrowRight, Loader2, ShieldCheck, Lock } from "lucide-react";

const HexMark = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M16 2.5 27.5 9v14L16 29.5 4.5 23V9L16 2.5Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M16 9 22 12.5v7L16 23l-6-3.5v-7L16 9Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" opacity="0.7" />
    <circle cx="16" cy="16" r="2" fill="white" />
  </svg>
);

export default function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async e => {
    e?.preventDefault?.();
    setLoading(true);
    setErr(null);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setErr(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-md p-8 relative overflow-hidden">
            <div className="absolute inset-0 ibp-grid-bg opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-sm val-gradient-warm text-white flex items-center justify-center shadow-sm">
                  <HexMark size={24} />
                </div>
                <div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-display font-bold lowercase tracking-tight text-valantic-black text-base">valantic</span>
                    <span className="text-valantic-silver">·</span>
                    <span className="font-display font-medium text-valantic-black text-sm">MDQ Analyzer</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-valantic-muted mt-0.5">
                    Sign in to continue
                  </div>
                </div>
              </div>

              <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900 mb-1">
                Welcome back
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                Authenticate with your assessor credentials.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <Field label="Email">
                  <input
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@ibp-agent.local"
                    className="w-full h-10 px-3 text-sm font-mono bg-white border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
                    required
                  />
                </Field>
                <Field label="Password">
                  <input
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="•••••••••••"
                    className="w-full h-10 px-3 text-sm font-mono bg-white border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
                    required
                  />
                </Field>

                {err && (
                  <div data-testid="login-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 font-mono break-words">
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  data-testid="login-submit-btn"
                  className={cn(
                    "w-full h-10 inline-flex items-center justify-center gap-2 rounded-sm text-sm font-medium transition-colors",
                    loading || !email || !password
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-valantic-black text-white hover:bg-slate-800"
                  )}
                >
                  {loading ? <Loader2 className="w-4 h-4 ibp-spin-slow" strokeWidth={1.75} /> : <ArrowRight className="w-4 h-4" strokeWidth={1.75} />}
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-[11px] text-slate-500">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600" strokeWidth={1.5} />
                  Read-only access to SAP
                </div>
                <div className="flex items-start gap-2">
                  <Lock className="w-3.5 h-3.5 mt-0.5 text-slate-500" strokeWidth={1.5} />
                  Encrypted session cookies
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-valantic-muted">
            valantic · MDQ Analyzer · v3.0
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-medium block mb-1.5">{label}</label>
    {children}
  </div>
);
